import React, { useState } from 'react';
import { apiService } from '../services/api';

interface ArtifactVersion {
  version: string;
  electron_tag: string;
  filename: string;
}

interface Props {
  siteName: string;
  versions: ArtifactVersion[];
  isLoading: boolean;
  onClose: () => void;
}

const DownloadIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
  </svg>
);

const LinkIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
  </svg>
);

const CopyIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
  </svg>
);

const BuildDownloadsModal: React.FC<Props> = ({ siteName, versions, isLoading, onClose }) => {
  const [downloading, setDownloading] = useState<string | null>(null);
  const [copying, setCopying]         = useState<string | null>(null);
  const [copied, setCopied]           = useState<string | null>(null);
  const [links, setLinks]             = useState<Record<string, string>>({});
  const [error, setError]             = useState<string | null>(null);

  const handleDownload = async (electronTag: string, filename: string) => {
    const key = `${electronTag}:${filename}`;
    setDownloading(key);
    setError(null);
    try {
      const { url } = await apiService.getArtifactUrl(siteName, electronTag, filename);
      window.open(url, '_blank');
    } catch (err: any) {
      setError(err?.response?.data?.error || `No artifact found for ${filename}`);
    } finally {
      setDownloading(null);
    }
  };

  const handleCopyLink = async (electronTag: string, filename: string) => {
    const key = `${electronTag}:${filename}`;
    // Re-copy from cache without a new API call
    if (links[key]) {
      await navigator.clipboard.writeText(links[key]);
      setCopied(key);
      setTimeout(() => setCopied(null), 2000);
      return;
    }
    setCopying(key);
    setError(null);
    try {
      const { url } = await apiService.getArtifactUrl(siteName, electronTag, filename, true);
      setLinks(prev => ({ ...prev, [key]: url }));
      await navigator.clipboard.writeText(url);
      setCopied(key);
      setTimeout(() => setCopied(null), 2000);
    } catch (err: any) {
      setError(err?.response?.data?.error || `Failed to generate link for ${filename}`);
    } finally {
      setCopying(null);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-[1003]"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg w-[90%] max-w-lg shadow-2xl"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex justify-between items-center py-4 px-5 border-b border-gray-200">
          <h3 className="text-lg text-gray-800 font-semibold m-0">
            Download Build — {siteName}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 text-2xl leading-none">&times;</button>
        </div>

        <div className="p-5 max-h-[60vh] overflow-y-auto">
          {error && (
            <p className="text-xs text-red-600 mb-3 text-center">{error}</p>
          )}
          {isLoading ? (
            <p className="text-sm text-gray-400 text-center py-6">Loading available builds…</p>
          ) : versions.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-6">No builds found in S3 for this site.</p>
          ) : (
            <div className="flex flex-col gap-2">
              {versions.map(({ version, electron_tag, filename }) => {
                const key  = `${electron_tag}:${filename}`;
                const link = links[key];
                return (
                  <div key={key} className="border border-gray-100 rounded-lg hover:bg-gray-50">
                    <div className="flex items-center justify-between px-3 py-2.5">
                      <div className="flex flex-col">
                        <span className="text-sm font-medium text-gray-800 font-mono">v{version}</span>
                        <span className="text-xs text-gray-400">{filename}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => handleCopyLink(electron_tag, filename)}
                          disabled={copying === key}
                          title="Copy 12-hour link"
                          className={`p-1 rounded text-xs transition-colors ${
                            copied === key
                              ? 'text-green-600 bg-green-50'
                              : 'text-slate-400 hover:text-blue-600 hover:bg-blue-50'
                          }`}
                        >
                          {copied === key ? (
                            <span className="text-[11px] font-medium px-0.5">Copied!</span>
                          ) : copying === key ? (
                            <span className="text-[11px] px-0.5">…</span>
                          ) : (
                            <LinkIcon />
                          )}
                        </button>
                        <button
                          onClick={() => handleDownload(electron_tag, filename)}
                          disabled={downloading === key}
                          title={`Download ${filename}`}
                          className="text-purple-600 hover:text-purple-800 disabled:opacity-40 p-1 rounded hover:bg-purple-50 transition-colors"
                        >
                          {downloading === key ? (
                            <span className="text-xs">…</span>
                          ) : (
                            <DownloadIcon />
                          )}
                        </button>
                      </div>
                    </div>

                    {link && (
                      <div className="px-3 pb-2.5 flex items-center gap-2 border-t border-gray-100 pt-2">
                        <span className="text-[11px] text-gray-400 truncate flex-1 font-mono select-all">{link}</span>
                        <button
                          onClick={() => { navigator.clipboard.writeText(link); setCopied(key); setTimeout(() => setCopied(null), 2000); }}
                          title="Copy again"
                          className="shrink-0 text-slate-400 hover:text-blue-600 transition-colors"
                        >
                          <CopyIcon />
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BuildDownloadsModal;
